import { Graphics } from 'pixi.js';
import { GAME_CONFIG, SymbolData } from '../config/GameConfig';
import { Reel } from './Reel';
import { Symbol } from './Symbol';

export interface WinResult {
  paylineIndex: number;
  payline: number[][];
  symbolId: string;
  matchCount: number;
  multiplier: number;
  winAmount: number;
  positions: { reel: number; row: number }[];
}

export class Paylines {
  private graphics: Graphics;
  private paylines: number[][][];
  
  constructor() {
    this.graphics = new Graphics();
    this.paylines = GAME_CONFIG.PAYLINES;
  }
  
  public evaluate(reels: Reel[], betPerLine: number): WinResult[] {
    const wins: WinResult[] = [];
    
    this.paylines.forEach((payline, paylineIndex) => {
      const win = this.evaluatePayline(reels, payline, paylineIndex, betPerLine);
      if (win) {
        wins.push(win);
      }
    });
    
    const scatterWins = this.evaluateScatters(reels, betPerLine);
    wins.push(...scatterWins);
    
    return wins;
  }
  
  private evaluatePayline(
    reels: Reel[],
    payline: number[][],
    paylineIndex: number,
    betPerLine: number
  ): WinResult | null {
    const symbols: Symbol[] = [];
    const positions: { reel: number; row: number }[] = [];
    
    for (let reelIndex = 0; reelIndex < payline.length; reelIndex++) {
      const [_, row] = payline[reelIndex];
      const symbol = reels[reelIndex].getSymbolAt(row);
      symbols.push(symbol);
      positions.push({ reel: reelIndex, row });
    }
    
    if (symbols.length === 0) return null;
    
    const firstSymbol = symbols[0];
    if (firstSymbol.symbolId === GAME_CONFIG.SCATTER_SYMBOL_ID) {
      return null;
    }
    
    let matchCount = 0;
    let currentSymbolId = firstSymbol.symbolId;
    
    for (let i = 0; i < symbols.length; i++) {
      const symbol = symbols[i];
      
      if (symbol.symbolId === GAME_CONFIG.WILD_SYMBOL_ID) {
        matchCount++;
        continue;
      }
      
      if (i === 0) {
        currentSymbolId = symbol.symbolId;
        matchCount = 1;
      } else if (symbol.symbolId === currentSymbolId || symbol.symbolId === GAME_CONFIG.WILD_SYMBOL_ID) {
        matchCount++;
      } else {
        break;
      }
    }
    
    if (matchCount >= 3) {
      const symbolData = this.getSymbolData(currentSymbolId);
      const multiplier = symbolData?.multipliers[matchCount] || 0;
      
      return {
        paylineIndex,
        payline,
        symbolId: currentSymbolId,
        matchCount,
        multiplier,
        winAmount: multiplier * betPerLine,
        positions
      };
    }
    
    return null;
  }
  
  private evaluateScatters(reels: Reel[], betPerLine: number): WinResult[] {
    const wins: WinResult[] = [];
    const scatterPositions: { reel: number; row: number }[] = [];
    
    for (let reelIndex = 0; reelIndex < reels.length; reelIndex++) {
      for (let row = 0; row < GAME_CONFIG.ROWS_COUNT; row++) {
        const symbol = reels[reelIndex].getSymbolAt(row);
        if (symbol.symbolId === GAME_CONFIG.SCATTER_SYMBOL_ID) {
          scatterPositions.push({ reel: reelIndex, row });
        }
      }
    }
    
    if (scatterPositions.length >= GAME_CONFIG.FREE_SPINS_TRIGGER) {
      const scatterData = this.getSymbolData(GAME_CONFIG.SCATTER_SYMBOL_ID);
      const multiplier = scatterData?.multipliers[scatterPositions.length] || 0;
      
      wins.push({
        paylineIndex: -1,
        payline: [],
        symbolId: GAME_CONFIG.SCATTER_SYMBOL_ID,
        matchCount: scatterPositions.length,
        multiplier,
        winAmount: multiplier * betPerLine * GAME_CONFIG.PAYLINES.length,
        positions: scatterPositions
      });
    }
    
    return wins;
  }
  
  private getSymbolData(symbolId: string): SymbolData | null {
    const allSymbols = [
      ...GAME_CONFIG.SYMBOLS.HIGH_VALUE,
      ...GAME_CONFIG.SYMBOLS.MEDIUM_VALUE,
      ...GAME_CONFIG.SYMBOLS.LOW_VALUE
    ];
    
    return allSymbols.find(s => s.id === symbolId) || null;
  }
  
  public drawPayline(paylineIndex: number): Graphics {
    const graphics = new Graphics();
    const payline = this.paylines[paylineIndex];
    
    if (!payline) return graphics;
    
    const { SYMBOL_SIZE, REEL_SPACING, ROWS_COUNT } = GAME_CONFIG;
    const reelWidth = SYMBOL_SIZE + REEL_SPACING;
    const symbolHeight = SYMBOL_SIZE;
    const totalWidth = reelWidth * (GAME_CONFIG.REELS_COUNT - 1);
    const totalHeight = symbolHeight * (ROWS_COUNT - 1);
    
    const startX = -totalWidth / 2;
    const startY = -totalHeight / 2;
    
    const colors = [
      0xFF0000, 0x00FF00, 0x0000FF, 0xFFFF00, 0xFF00FF,
      0x00FFFF, 0xFFA500, 0x800080, 0x008080, 0xFFC0CB,
      0x808080, 0xFFD700, 0x008000, 0x000080, 0x800000,
      0x00FF7F, 0xFF1493, 0x1E90FF, 0xFFDAB9, 0x7B68EE
    ];
    
    graphics.setStrokeStyle({ width: 4, color: colors[paylineIndex % colors.length] });
    
    payline.forEach((pos, index) => {
      const [reel, row] = pos as [number, number];
      const x = startX + reel * reelWidth;
      const y = startY + row * symbolHeight;
      
      if (index === 0) {
        graphics.moveTo(x, y);
      } else {
        graphics.lineTo(x, y);
      }
    });
    
    graphics.stroke();
    
    return graphics;
  }
  
  public getGraphics(): Graphics {
    return this.graphics;
  }
  
  public clear(): void {
    this.graphics.clear();
  }
}